
import { createClient } from '@supabase/supabase-js';
import { hasValidSupabaseConfig } from './supabaseDebug';
import { supabase as supabaseClient, ensureBucketExists, hasDetectedRLS, getCorrectPublicUrl } from '@/integrations/supabase/client';

// Define bucket names
export const BUCKETS = {
  PORTFOLIO_MEDIA: 'portfolio-media',
  PARTNER_LOGOS: 'partner-logos',
};

export { getCorrectPublicUrl };

// Re-export the Supabase client
export const supabase = supabaseClient;

// Connection health monitoring
let lastSuccessfulConnection = Date.now();
let connectionFailures = 0;
const MAX_CONNECTION_FAILURES = 5;
let bucketCreationRLSDetected = false;
let bucketsCreated = false;

// Updated function to initialize all required buckets
export const initializeBuckets = async () => {
  try {
    console.log("Checking Supabase storage connection...");
    
    // Check if we have valid Supabase configuration
    if (!hasValidSupabaseConfig()) {
      console.log("Using local storage fallback - Invalid Supabase configuration");
      return { 
        success: false, 
        message: "Supabase not configured properly. Using local storage fallback.",
        error: { message: "Invalid Supabase configuration" },
        hasRlsLimitations: false
      };
    }
    
    // Check if we've had too many connection failures recently
    if (connectionFailures >= MAX_CONNECTION_FAILURES && 
        (Date.now() - lastSuccessfulConnection) < 60000) {
      console.log("Too many recent connection failures, using local storage temporarily");
      return {
        success: false,
        message: "Too many recent connection failures. Using local storage temporarily.",
        error: { message: "Exceeded connection failure threshold" },
        hasRlsLimitations: false
      };
    }
    
    // Check if RLS issues have already been detected by the client
    if (hasDetectedRLS()) {
      bucketCreationRLSDetected = true;
      console.log("RLS issues already detected by client, assuming buckets exist due to SQL migration");
      return {
        success: true,
        message: "Connected to Supabase with RLS limitations, buckets should now work for reading.",
        hasRlsLimitations: true
      };
    }
    
    // If buckets were successfully created in a previous session, skip bucket creation
    if (bucketsCreated) {
      console.log("Buckets already created in this session");
      return {
        success: true,
        message: "Buckets already initialized.",
        hasRlsLimitations: bucketCreationRLSDetected
      };
    }
    
    // Try a simple operation to check if Supabase is working, with a timeout
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Connection timeout')), 5000);
      });
      
      // Test connection by trying to list buckets
      const testPromise = supabase.storage.listBuckets();
      
      await Promise.race([testPromise, timeoutPromise]);
      
      lastSuccessfulConnection = Date.now();
      connectionFailures = 0;
      console.log("Basic Supabase connectivity check passed");
    } catch (connError) {
      connectionFailures++;
      console.error("Basic Supabase connectivity failed:", connError);
      return {
        success: false,
        message: "Cannot connect to Supabase. Using local storage fallback.",
        error: connError,
        hasRlsLimitations: false
      };
    }
    
    // Now check if the buckets exist - we expect them to exist after running the SQL
    console.log("Ensuring portfolio media bucket exists...");
    try {
      const portfolioMediaResult = await ensureBucketExists(BUCKETS.PORTFOLIO_MEDIA, true);
      
      if (!portfolioMediaResult.success && portfolioMediaResult.error) {
        console.warn("Failed to check/create portfolio media bucket:", portfolioMediaResult.error);
        bucketCreationRLSDetected = true;
      }
    } catch (err) {
      console.error("Error ensuring portfolio media bucket:", err);
      bucketCreationRLSDetected = true;
    }
    
    // Check partner logos bucket
    console.log("Ensuring partner logos bucket exists...");
    try {
      const partnerLogosResult = await ensureBucketExists(BUCKETS.PARTNER_LOGOS, true);
      
      if (!partnerLogosResult.success && partnerLogosResult.error) {
        console.warn("Failed to check/create partner logos bucket:", partnerLogosResult.error);
        bucketCreationRLSDetected = true;
      }
    } catch (err) {
      console.error("Error ensuring partner logos bucket:", err);
      bucketCreationRLSDetected = true;
    }
    
    // Mark buckets as created even if there were RLS issues, since the SQL migration should have created them
    bucketsCreated = true;
    
    // Even if we detect RLS issues, the SQL migration should have created the buckets,
    // so we can continue with better expectations
    return {
      success: true,
      message: "Connected to Supabase. Storage should now work for reading files.",
      hasRlsLimitations: bucketCreationRLSDetected
    };
    
  } catch (error) {
    connectionFailures++;
    console.error('Failed to initialize buckets:', error);
    return { 
      success: false, 
      error,
      message: "Failed to initialize storage buckets. Check Supabase configuration.",
      hasRlsLimitations: false
    };
  }
};

// Helper function to determine if we should use local storage
export const shouldUseLocalStorage = () => {
  // For file viewing, ALWAYS prefer Supabase if config is valid
  const isReadOperation = true;
  
  if (hasValidSupabaseConfig() && isReadOperation) {
    return false; // Use Supabase for all read operations
  }
  
  // Only use local storage if there are connection failures or we haven't been able to verify buckets
  return connectionFailures >= MAX_CONNECTION_FAILURES || 
         (Date.now() - lastSuccessfulConnection > 30000) ||
         !bucketsCreated;
};

// Expose connection health status
export const getConnectionHealth = () => {
  return {
    connectionFailures,
    lastSuccessfulConnection,
    isHealthy: connectionFailures < MAX_CONNECTION_FAILURES,
    timeSinceLastSuccess: Date.now() - lastSuccessfulConnection,
    hasRlsLimitations: bucketCreationRLSDetected,
    bucketsCreated
  };
};

// Reset connection health status
export const resetConnectionHealth = () => {
  connectionFailures = 0;
  lastSuccessfulConnection = Date.now();
  return { success: true };
};

// Reset detected RLS issues and bucket creation status
export const resetRLSDetection = () => {
  bucketCreationRLSDetected = false;
  bucketsCreated = false;
  return { success: true };
};

// Helper function to verify if a file exists in a bucket
export const checkFileExists = async (bucket: string, filePath: string): Promise<boolean> => {
  try {
    // Normalize path - remove leading slashes
    const normalizedPath = filePath.replace(/^\/+/, '');
    
    // Use the Supabase list method to check if the file exists
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(normalizedPath.split('/').slice(0, -1).join('/') || '');
    
    if (error) {
      console.error('Error checking if file exists:', error);
      return false;
    }
    
    const fileName = normalizedPath.split('/').pop() || '';
    return data?.some(file => file.name === fileName) || false;
  } catch (err) {
    console.error('Exception checking if file exists:', err);
    return false;
  }
};
